export const VISUAL_THEMES = [
  'trading_floor',
  'kitchen',
  'construction',
  'ancient_map',
  'puzzle_world',
  'dialogue_stage',
  'adventure',
] as const;

export type VisualTheme = (typeof VISUAL_THEMES)[number];

export type ThemeConfig = {
  icon: string;
  label: string;
  fallbackColor: string;
  gradientColors: [string, string, string];
  environmentLabel: string;
  environmentEmoji: string;
  domainKeywords: string[];
  bossName: string;
  bossEmoji: string;
  heroEmoji: string;
  atmosphereWords: string[];
  surfaceHint: string;
};

export const THEME_CONFIG: Record<VisualTheme, ThemeConfig> = {
  trading_floor: {
    icon: '📈',
    label: 'זירת מסחר',
    fallbackColor: '#1a3a2a',
    gradientColors: ['#0A1628', '#0D2137', '#112A1A'],
    environmentLabel: 'הגעת לזירת המסחר',
    environmentEmoji: '🏦',
    domainKeywords: ['שוק', 'מניה', 'תיק', 'פוזיציה', 'רווח', 'הפסד', 'אנליזה'],
    bossName: 'ראש השוק',
    bossEmoji: '🐂',
    heroEmoji: '📊',
    atmosphereWords: ['ticker', 'ירוק', 'אדום', 'עמוד'],
    surfaceHint: 'chart or dashboard',
  },
  kitchen: {
    icon: '👨‍🍳',
    label: 'מטבח',
    fallbackColor: '#3a1a1a',
    gradientColors: ['#2A1208', '#1E0E06', '#3A1A08'],
    environmentLabel: 'ברוך הבא למטבח המקצועי',
    environmentEmoji: '🍳',
    domainKeywords: ['מתכון', 'טכניקה', 'חומרי גלם', 'טעם', 'מרקם', 'בישול', 'אפייה'],
    bossName: 'השף הראשי',
    bossEmoji: '👨‍🍳',
    heroEmoji: '🔪',
    atmosphereWords: ['ריח', 'חום', 'טקסטורה', 'ציוד'],
    surfaceHint: 'document or workflow',
  },
  construction: {
    icon: '🏗️',
    label: 'סטודיו בנייה',
    fallbackColor: '#1a2a3a',
    gradientColors: ['#0E1A28', '#162233', '#1A2A1A'],
    environmentLabel: 'אתר הבנייה פעיל',
    environmentEmoji: '🏗️',
    domainKeywords: ['תכנית', 'מבנה', 'חומרים', 'בטיחות', 'תקנים', 'פרויקט', 'ביצוע'],
    bossName: 'מנהל הפרויקט',
    bossEmoji: '🦺',
    heroEmoji: '⚙️',
    atmosphereWords: ['קרקע', 'מפה', 'ברזל', 'בטון'],
    surfaceHint: 'workflow or document',
  },
  ancient_map: {
    icon: '🗺️',
    label: 'מסע בזמן',
    fallbackColor: '#2a1a0a',
    gradientColors: ['#1A0E04', '#251505', '#2A1A0A'],
    environmentLabel: 'המסע בזמן מתחיל',
    environmentEmoji: '🗺️',
    domainKeywords: ['היסטוריה', 'עידן', 'גילוי', 'ציוויליזציה', 'תרבות', 'מסמך', 'עדות'],
    bossName: 'שומר הידע',
    bossEmoji: '🏛️',
    heroEmoji: '🔍',
    atmosphereWords: ['עתיק', 'כתב', 'ארכיב', 'חפירה'],
    surfaceHint: 'document or conversation',
  },
  puzzle_world: {
    icon: '🧩',
    label: 'עולם פאזל',
    fallbackColor: '#1a1a3a',
    gradientColors: ['#0F0F28', '#14143A', '#1A1A2E'],
    environmentLabel: 'החידה מחכה לפתרון',
    environmentEmoji: '🧩',
    domainKeywords: ['חידה', 'לוגיקה', 'דפוס', 'פתרון', 'ניתוח', 'קשר', 'גילוי'],
    bossName: 'מאסטר הפאזל',
    bossEmoji: '🧠',
    heroEmoji: '💡',
    atmosphereWords: ['חלקים', 'פאזל', 'מפתח', 'פתרון'],
    surfaceHint: 'workflow or code',
  },
  dialogue_stage: {
    icon: '🎭',
    label: 'במה',
    fallbackColor: '#2a0a2a',
    gradientColors: ['#1A0A1A', '#220D22', '#2A1028'],
    environmentLabel: 'הבמה מוארת',
    environmentEmoji: '🎭',
    domainKeywords: ['דיאלוג', 'תקשורת', 'מסר', 'קהל', 'עמדה', 'שכנוע', 'ביטחון'],
    bossName: 'מנחה הוויכוח',
    bossEmoji: '🎤',
    heroEmoji: '🗣️',
    atmosphereWords: ['נאום', 'שיחה', 'שכנוע', 'קשר'],
    surfaceHint: 'conversation',
  },
  adventure: {
    icon: '⚔️',
    label: 'הרפתקה',
    fallbackColor: '#0f0f1a',
    gradientColors: ['#0A0A14', '#0F0F1E', '#141426'],
    environmentLabel: 'המסע מתחיל',
    environmentEmoji: '⚔️',
    domainKeywords: ['אתגר', 'מיומנות', 'ניסיון', 'צמיחה', 'ניצחון', 'כוח', 'תושייה'],
    bossName: 'האתגר הסופי',
    bossEmoji: '🐉',
    heroEmoji: '⚔️',
    atmosphereWords: ['הרפתקה', 'גיבור', 'כוח', 'ניצחון'],
    surfaceHint: 'workflow',
  },
};

export const getThemeConfig = (theme?: string | null): ThemeConfig => {
  if (!theme || !VISUAL_THEMES.includes(theme as VisualTheme)) {
    return THEME_CONFIG.adventure;
  }
  return THEME_CONFIG[theme as VisualTheme];
};
